import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Mail, Search, Sparkles, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NewsletterCard from "@/components/newsletter/NewsletterCard";
import { newsletterApi } from "@/lib/newsletterApi";

const BlogPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [subscriberForm, setSubscriberForm] = useState({ name: "", email: "" });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setLoading(true);
        const data = await newsletterApi.getPublishedBlogs();
        setBlogs(data.blogs || []);
        setCategories(["All", ...(data.categories || [])]);
      } catch (error) {
        setMessage(
          error.response?.data?.message || "Unable to load blogs right now.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  const filteredBlogs = useMemo(
    () =>
      blogs.filter((blog) => {
        const matchesCategory =
          selectedCategory === "All" || blog.category === selectedCategory;
        const normalizedSearch = deferredSearch.trim().toLowerCase();

        if (!normalizedSearch) {
          return matchesCategory;
        }

        return (
          matchesCategory &&
          [blog.title, blog.excerpt, ...(blog.tags || [])]
            .join(" ")
            .toLowerCase()
            .includes(normalizedSearch)
        );
      }),
    [blogs, deferredSearch, selectedCategory],
  );

  const featuredBlog =
    filteredBlogs.find((item) => item.isFeatured) || filteredBlogs[0];
  const remainingBlogs = featuredBlog
    ? filteredBlogs.filter((item) => item._id !== featuredBlog._id)
    : [];

  const handleSubscribe = async (event) => {
    event.preventDefault();

    try {
      await newsletterApi.subscribe(subscriberForm);
      setMessage(
        "You’re subscribed. New Grawish blogs will reach your inbox automatically.",
      );
      setSubscriberForm({ name: "", email: "" });
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
          "Subscription failed. Please try again.",
      );
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar onContactClick={() => navigate("/contact")} />

      <section className="newsletter-grid-bg relative overflow-hidden pt-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(224,64,251,0.16),_transparent_30%),radial-gradient(circle_at_bottom_left,_rgba(123,31,162,0.12),_transparent_35%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-purple-100 bg-white/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-purple-700 backdrop-blur">
              <Sparkles className="h-4 w-4" />
              Grawish Blog
            </div>
            <h1 className="font-heading text-3xl font-black leading-tight text-gray-900 sm:text-5xl">
              Deeper articles on growth, brand building, AI, and digital
              execution.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">
              The blog is now fully dynamic too, with dedicated listing and
              detail pages plus subscriber email on publish.
            </p>

            <button
              type="button"
              onClick={() => navigate("/contact")}
              className="inline-flex items-center gap-2 primary-btn mt-6 px-6 py-3 rounded-full text-white font-semibold text-sm"
            >
              Book Consultation
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-7 grid gap-4 rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_18px_50px_rgba(123,31,162,0.08)] backdrop-blur md:grid-cols-[1fr_auto] md:items-center">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <label className="relative min-w-[260px] flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search blogs"
                  className="w-full rounded-full border border-purple-100 bg-white px-11 py-2.5 text-sm text-gray-700 outline-none transition focus:border-purple-300"
                />
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setSelectedCategory(category)}
                    className={`rounded-full px-3.5 py-2 text-xs font-semibold transition sm:text-sm ${
                      selectedCategory === category
                        ? "bg-gradient-to-r from-pink-500 to-purple-700 text-white shadow-lg shadow-purple-300/30"
                        : "bg-purple-50 text-purple-700 hover:bg-purple-100"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-[20px] bg-gray-900 px-4 py-3 text-white">
              <div className="text-2xl font-black">{filteredBlogs.length}</div>
              <div className="text-xs text-gray-300 sm:text-sm">
                Live blog posts
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {loading ? (
          <div className="rounded-[28px] border border-purple-100 bg-purple-50/60 p-10 text-center text-gray-600">
            Loading blogs...
          </div>
        ) : featuredBlog ? (
          <div className="space-y-6">
            <NewsletterCard
              article={{ ...featuredBlog, slug: featuredBlog.slug }}
              featured
              linkBase="/blog"
            />
            <div className="grid gap-5 lg:grid-cols-3">
              {remainingBlogs.map((blog) => (
                <NewsletterCard
                  key={blog._id}
                  article={blog}
                  linkBase="/blog"
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-[28px] border border-dashed border-purple-200 bg-white p-14 text-center">
            <h2 className="font-heading text-3xl font-bold text-gray-900">
              No blogs found
            </h2>
            <p className="mt-3 text-gray-600">
              Create one from the admin blog page and it will appear here
              automatically.
            </p>
          </div>
        )}

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 overflow-hidden rounded-[26px] bg-gray-900"
        >
          <div className="grid gap-0 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-purple-200">
                <BookOpen className="h-4 w-4" />
                Blog updates
              </div>
              <h2 className="mt-4 font-heading text-3xl font-black text-white">
                Get every new Grawish blog post the moment it goes live.
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-6 text-gray-300 sm:text-base">
                Subscribers receive blog release emails too, so one audience can
                stay updated on both newsletters and blogs.
              </p>
            </div>

            <form onSubmit={handleSubscribe} className="bg-white p-6 sm:p-8">
              <h3 className="font-heading text-xl font-bold text-gray-900">
                Join the list
              </h3>
              <div className="mt-4 space-y-3">
                <input
                  value={subscriberForm.name}
                  onChange={(event) =>
                    setSubscriberForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="Your name"
                  className="w-full rounded-xl border border-purple-100 px-3.5 py-3 text-sm outline-none transition focus:border-purple-300"
                />
                <input
                  type="email"
                  required
                  value={subscriberForm.email}
                  onChange={(event) =>
                    setSubscriberForm((current) => ({
                      ...current,
                      email: event.target.value,
                    }))
                  }
                  placeholder="Your email"
                  className="w-full rounded-xl border border-purple-100 px-3.5 py-3 text-sm outline-none transition focus:border-purple-300"
                />
                <button
                  type="submit"
                  className="primary-btn w-full rounded-xl px-4 py-3 text-sm font-bold text-white"
                >
                  Subscribe now
                </button>
                {message ? (
                  <p className="text-sm text-gray-600">{message}</p>
                ) : null}
              </div>
            </form>
          </div>
        </motion.section>
      </main>

      <Footer onContactClick={() => navigate("/contact")} />
    </div>
  );
};

export default BlogPage;
